/** passive event feature detection */
import {
  ADMIN_TIMEZONE_OFFSET,
  MS_PER_DAY,
  MS_PER_FIVE_MINUTES,
  MS_PER_HOUR,
  MS_PER_MINUTE,
} from 'Root/helpers/DateConstants';
import HttpErrorModel from 'Models/HttpErrorModel';
import CurrencyModel from 'Models/CurrencyModel';
import AppModel from 'Models/AppModel';

import axios from 'axios';
import dayjs from 'dayjs';

let supportsPassive = false;
try {
  const opts = Object.defineProperty({}, 'passive', {
    get() { // eslint-disable-line
      supportsPassive = true;
    },
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
// eslint-disable-next-line no-empty
} catch (e) {
}

export const supportsPassiveEvent = supportsPassive;

/** scrollTo with smooth behavior (if supported), with fallback to old version */
const supportsNativeSmoothScroll = typeof document !== 'undefined'
  && 'scrollBehavior' in document.documentElement.style;

export function scrollTo(x, y, element) {
  if (typeof element === 'undefined') {
    element = window;
  }

  if (supportsNativeSmoothScroll) {
    element.scrollTo({
      top: y,
      left: x,
      behavior: 'smooth',
    });
  } else {
    element.scrollTo(x, y);
  }
}

export function scrollIntoView(node) {
  if (supportsNativeSmoothScroll) {
    node.scrollIntoView({
      behavior: 'smooth',
    });
  } else {
    node.scrollIntoView();
  }
}

export function scrollToAnchorHelper(node, offset, smoothScroll = true) {
  const bodyRect = document.body.getBoundingClientRect();
  const elemRect = node.getBoundingClientRect();
  const targetPosition = elemRect.top - bodyRect.top - offset;

  window.scrollTo({
    top: targetPosition,
    behavior: smoothScroll ? 'smooth' : 'instant',
  });
}

export function scrollIntoViewWithCallback(node, onStart, onEnd, offset = 20) {
  if (supportsNativeSmoothScroll) {
    if (typeof onStart === 'function') {
      onStart();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (typeof onEnd === 'function') {
            onEnd();
          }
          observer.disconnect();
        }
      },
      {
        threshold: 1.0,
      },
    );

    observer.observe(node);
    scrollToAnchorHelper(node, offset);
  } else {
    node.scrollIntoView();

    if (typeof onEnd === 'function') {
      onEnd();
    }
  }
}

// simple mouse detect
export const hasMouse = typeof window !== 'undefined'
  ? matchMedia('(pointer:fine)').matches
  : false;

export const hasTouch = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.msMaxTouchPoints);

export function slugify(text, search = ' ', replace = '-') {
  return `${text.toLowerCase()
    .split(search)
    .join(replace)}`;
}

export function _debounce(func, wait, options = {}) {
  let lastArgs;
  let lastThis;
  let result;
  let timerId;
  let lastCallTime;
  let lastInvokeTime = 0;
  let leading = false;
  let maxing = false;
  let trailing = true;

  wait = Number.parseInt(wait, 10) || 0;

  leading = !!options.leading;
  maxing = 'maxWait' in options;
  const maxWait = maxing ? Math.max(Number.parseInt(options.maxWait, 10) || 0, wait) : null;
  trailing = 'trailing' in options ? !!options.trailing : trailing;

  function invokeFunc(time) {
    const args = lastArgs;
    const thisArg = lastThis;

    // eslint-disable-next-line no-multi-assign
    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait)
      || (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  // eslint-disable-next-line consistent-return
  function timerExpired() {
    const time = Date.now();

    if (shouldInvoke(time)) {
      // eslint-disable-next-line no-use-before-define
      return trailingEdge(time);
    }

    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }

    // eslint-disable-next-line no-multi-assign
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }

    lastInvokeTime = 0;
    // eslint-disable-next-line no-multi-assign
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(Date.now());
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }

      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }

    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }

    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;

  return debounced;
}

export function _throttle(func, wait, options = {}) {
  let leading = true;
  let trailing = true;

  leading = 'leading' in options ? !!options.leading : leading;
  trailing = 'trailing' in options ? !!options.trailing : trailing;

  return _debounce(func, wait, {
    leading,
    maxWait: wait,
    trailing,
  });
}

export function throttle(func, limit, ...args) {
  let lastFunc;
  let lastRun;
  return () => {
    const context = this;
    if (!lastRun) {
      func.apply(context, args);
      lastRun = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if ((Date.now() - lastRun) >= limit) {
          func.apply(context, args);
          lastRun = Date.now();
        }
      }, limit - (Date.now() - lastRun));
    }
  };
}

export function makeTagPathArray(tag, tags, arr = []) {
  if (tag.parent_id) {
    const parentTag = tags.find((t) => t.id === tag.parent_id);
    if (parentTag) {
      makeTagPathArray(parentTag, tags, arr);
    }
  }

  arr.push(tag.seo_url);

  return arr;
}

export function makeTagPathArrayByParent(tag, arr = []) {
  if (tag.parent?.id) {
    const parentTag = tag.parent;
    if (parentTag) {
      arr.push(parentTag.seo_url);
      makeTagPathArray(parentTag, arr);
    }
  }

  arr.push(tag.seo_url);
  return arr;
}

export function splitUrl(context) {
  return (typeof context !== 'undefined' && context.url)
    ? context.url.split('/') // server side
    : window.location.pathname.split('/'); // client side
}

// this is much faster than splitting strings
export function floatDecimalsCount(a) {
  if (typeof a !== 'number' || !Number.isFinite(a)) {
    return 0;
  }

  let e = 1;
  let p = 0;

  while (Math.round(a * e) / e !== a) {
    e *= 10;
    p += 1;
  }

  return p;
}

/**
 * This method splits float to parts and returns an object with all info
 *
 * @param {Number|String} number
 * @param {Number} decimals
 * @returns {{decimalInt: number, decimal: number, int: number, full: number}}
 */
export function splitFloat(number, decimals = null) {
  let float = parseFloat(number);
  let precision;
  let floatDecimals = decimals;
  if (floatDecimals !== null && Number(floatDecimals) >= 1) {
    precision = 10 ** floatDecimals;
  } else {
    floatDecimals = floatDecimalsCount(float);
    precision = 10 ** floatDecimals;
  }

  float = Math.round(float * precision) / precision;
  const int = Math.trunc(float);
  const decimal = Number(Math.round((float - int) * precision) / precision);
  const decimalInt = Math.round(decimal * precision);

  return {
    int,
    decimal,
    decimalInt,
    full: float,
  };
}

// this is 2 times slower
// function splitFloat(number, decimals = null) {
//   let float = parseFloat(number);
//   let floatDecimals = decimals;
//   if (floatDecimals === null || Number(floatDecimals) <= 1) {
//     floatDecimals = floatDecimalsCount(float);
//   }
//
//   float = +float.toFixed(floatDecimals);
//   const int = Math.trunc(float);
//   const decimal = +(float % 1).toFixed(floatDecimals);
//   const decimalInt = Math.round(decimal * 10 ** floatDecimals);
//
//   return {
//     int,
//     decimal,
//     decimalInt,
//     full: float,
//   };
// }

export function capitalize(str, total = false) {
  str = str || ' ';
  return total
    ? str.split(' ')
      .map((s) => `${s[0].toUpperCase()}${s.slice(1)}`)
    : `${str[0].toUpperCase()}${str.slice(1)}`;
}

/**
 * Method calculating real string size with passed font settings using canvas
 *
 * @param text
 * @param font full font string example: 500 15px Rubik
 * @returns {number}
 */
export function getTextWidth(text, font) {
  if (typeof window === 'undefined') {
    return 0;
  }
  // re-use canvas object for better performance
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  context.font = font;

  const metrics = context.measureText(text !== '' ? text : null);
  return metrics.width;
}

export function formatStringByObject(str, formatObj) {
  if (!formatObj || !str) {
    return null;
  }

  return str.replace(new RegExp(/{[a-z|{_,.}]+}/, 'gi'), (m) => {
    const key = m.slice(1, m.length - 1);
    const nestedKeys = key.split('.');
    let value = formatObj;
    for (let i = 0; i < nestedKeys.length; i++) {
      value = value[nestedKeys[i]];
      if (!value) {
        break;
      }
    }
    return value ?? m;
  });
}

export function formatDateToMonthForDb(date) {
  const dateTimeFormat = new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  /* eslint-disable no-unused-vars */
  const [
    { value: month }, ,
    { value: day }, ,
    { value: year }, ,
    { value: hour }, ,
    { value: minute }, ,
    { value: second }, ,
  ] = dateTimeFormat.formatToParts(date);
  /* eslint-enable no-unused-vars */

  return `${year}-${month}`;
}

export function formatNumberAsMoney(value, options) {
  if (!options) {
    options = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  }

  return value.toLocaleString(undefined, options);
}

export function getDateParts(date, inUtc) {
  if (!(date instanceof Date)) {
    date = new Date(date.replace(' ', 'T'));
  }

  const day = (inUtc ? date.getUTCDate() : date.getDate()).toString().padStart(2, '0');
  const month = ((inUtc ? date.getUTCMonth() : date.getMonth()) + 1).toString().padStart(2, '0');
  const year = (inUtc ? date.getUTCFullYear() : date.getFullYear()).toString();
  const hour = ((inUtc ? date.getUTCHours() : date.getHours()).toString() % 24).toString().padStart(2, '0');
  const minute = (inUtc ? date.getUTCMinutes() : date.getMinutes()).toString().padStart(2, '0');
  const second = (inUtc ? date.getUTCSeconds() : date.getSeconds()).toString().padStart(2, '0');

  return {
    day,
    month,
    year,
    hour,
    minute,
    second,
  };
}

export function formatDateForDB(date, shouldIncludeTime = true, inUtc = false) {
  if (typeof date === 'string') {
    date = new Date(date.replace(' ', 'T'));
  }

  if (typeof date !== 'object') {
    date = new Date(date);
  }

  const {
    day,
    month,
    year,
    hour,
    minute,
    second,
  } = getDateParts(date, inUtc);

  let formattedDate = null;
  if (shouldIncludeTime) {
    formattedDate = `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  } else {
    formattedDate = `${year}-${month}-${day}`;
  }

  return formattedDate;
}

export function adjustToBrowserTimezone(date, timezoneOffset) {
  if (!date) {
    return null;
  }

  const resultDate = new Date(date);
  const offsetInMinutes = timezoneOffset ?? resultDate.getTimezoneOffset();

  resultDate.setMinutes(resultDate.getMinutes() - offsetInMinutes);

  return resultDate;
}

export function adjustToAdminTimezone(date) {
  if (!date) {
    return null;
  }

  const resultDate = new Date(date);
  const offsetInMinutes = resultDate.getTimezoneOffset();

  resultDate.setMinutes(resultDate.getMinutes() - offsetInMinutes + ADMIN_TIMEZONE_OFFSET);

  return resultDate;
}

export function adjustTimezone(data, dateFields = []) {
  if (typeof data === 'object' && dateFields.length) {
    const adjustedDates = Object.fromEntries(dateFields.map((field) => [field, adjustToBrowserTimezone(data[field])]));

    return {
      ...data,
      ...adjustedDates,
    };
  }

  return adjustToBrowserTimezone(data);
}

export function utcDateToMoscowTimezone(value = null, format = 'string') {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  date.setMinutes(date.getMinutes() - ADMIN_TIMEZONE_OFFSET);

  if (format === 'date') {
    return date;
  }

  return date.toLocaleString();
}

export function isUserHasRole(user, roleId) {
  if (!user) {
    return false;
  }

  if (typeof roleId === 'string' || typeof roleId === 'number') {
    return +user.role === +roleId;
  }

  if (Array.isArray(roleId)) {
    return roleId.includes(+user.role);
  }

  return false;
}

export function loadScriptAsync(scriptUrl) {
  return new Promise((resolve, reject) => {
    if (!scriptUrl) {
      reject();
      return;
    }
    const scriptNode = document.createElement('script');
    scriptNode.onload = resolve;
    document.head.appendChild(scriptNode);
    scriptNode.src = scriptUrl;
  });
}

export function executeAfterLoad(fn) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!window.performance) {
    fn();
    return;
  }

  const navData = window.performance.getEntriesByType('navigation');
  if (navData.length > 0 && navData[0].loadEventEnd > 0) {
    fn();
  } else {
    window.addEventListener('load', () => {
      fn();
    });
  }
}

export function chunkArray(array, chunkSize) {
  const results = [];
  const arrayCopy = [...array];

  while (arrayCopy.length) {
    results.push(arrayCopy.splice(0, chunkSize));
  }

  return results;
}

export function getFirstDayOfMonth(date) {
  const today = date ? new Date(date) : new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1);
}

export function getLastDayOfMonth(date) {
  const today = date ? new Date(date) : new Date();
  return new Date(today.getFullYear(), today.getMonth() + 1, 0);
}

export function getFirstDayOfWeek(date) {
  const today = date ? new Date(date) : new Date();
  const day = today.getDay();
  const diff = today.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(today.setDate(diff));
}

export function getLastDayOfWeek(date) {
  const today = date ? new Date(date) : new Date();
  const day = today.getDay();
  const diff = today.getDate() - day + 7;
  return new Date(today.setDate(diff));
}

export function uuidv4() {
  if (typeof window === 'undefined') {
    return;
  }

  /* eslint-disable */
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => (c ^ window.crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
  /* eslint-enable */
}

export function canSupportApplePay() {
  // eslint-disable-next-line no-undef
  return !!globalThis?.ApplePaySession?.canMakePayments();
}

export function tagsSort(a, b) {
  let sort = 0;

  // более устойчивая сортировка, иначе в мозилле падало с регидрациями
  if (a.priority > b.priority) {
    sort = -1;
  } else if (a.priority < b.priority) {
    sort = 1;
  } else if (a.title < b.title) {
    sort = -1;
  } else if (a.title > b.title) {
    sort = 1;
  }

  return sort;
}

/**
 * Проставляет 404 состояние как на SSR так и на SPA
 * @param {Object} store
 * @param {Object=} context
 */
export function setNotFoundState(store, context) {
  if (context && !context._is404) {
    context._is404 = true;
  }

  if (store.state.httpErrorCode !== HttpErrorModel.NOT_FOUND) {
    store.commit('mutateHttpErrorCode', HttpErrorModel.NOT_FOUND);
  }
}

export function round(value, decimals = 2) {
  const precision = 10 ** decimals;
  return Math.round(value * precision) / precision;
}

export function roundDateMinutes(date, msRoundTo = MS_PER_FIVE_MINUTES) {
  return new Date(Math.round(date / msRoundTo) * msRoundTo);
}

export function transformBigNumber(number) {
  let precision = 0;
  let suffix = '';

  if (number >= 1000000) {
    precision = 1000000;
    suffix = 'M';
  } else if (number >= 10000) {
    precision = 1000;
    suffix = 'K';
  }

  return precision > 0 ? `${round(number / precision)}${suffix}` : number;
}

export function formatDateToString(timeString) {
  const date = adjustToBrowserTimezone(new Date(timeString));
  const {
    day, month, year, hour, minute,
  } = getDateParts(date);

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'];

  const dayOfWeek = days[date.getDay()];
  const monthName = months[parseInt(month, 10) - 1];

  return {
    dayOfWeek,
    fullDate: `${monthName} ${parseInt(day, 10)}, ${year}`,
    time: `${hour}:${minute}`,
  };
}

// Internal helper to generate functions for escaping and unescaping strings
// to/from HTML interpolation.
function createEscaper(map) {
  const escaper = (match) => map[match];
  // Regexes for identifying a key that needs to be escaped.
  const source = `(?:${Object.keys(map).join('|')})`;
  const testRegexp = RegExp(source);
  const replaceRegexp = RegExp(source, 'g');
  return (string) => {
    string = string == null ? '' : string;
    return testRegexp.test(string) ? string.replace(replaceRegexp, escaper) : string;
  };
}

const unescapeMap = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#x27;': "'",
  '&#x60;': '`',
  '&nbsp;': ' ',
  '&rsquo;': '’',
  '&ldquo;': '“',
  '&rdquo;': '”',
};

const unescape = createEscaper(unescapeMap);

export function getTextFromHTML(string) {
  return unescape(string.replace(/<\/?[\w\s="/.':;#-/?]+>/gi, ''));
}

// Page Visibility API, set names of hidden property and change event
export function setWindowVisibilityAPI() {
  if (window.visibilityAPI !== undefined) {
    return;
  }

  let hidden = null;
  let event = null;

  if (typeof document.hidden !== 'undefined') {
    hidden = 'hidden';
    event = 'visibilitychange';
  } else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden';
    event = 'msvisibilitychange';
  } else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden';
    event = 'webkitvisibilitychange';
  }

  window.visibilityAPI = { hidden, event };
}

export function calcFPS() {
  return new Promise((resolve) => {
    requestAnimationFrame((t1) => {
      requestAnimationFrame((t2) => {
        resolve(1000 / (t2 - t1));
      });
    });
  });
}

export function setWindowFPS() {
  setTimeout(() => {
    calcFPS()
      .then((fps) => {
        window._maxFPS = fps;
      });
  }, 1000);
}

export function formatCurrencyString(value, regionId) {
  const currencyData = CurrencyModel.ID_TO_DATA[regionId];

  const options = {
    style: 'currency',
    currency: currencyData.title,
    minimumFractionDigits: 2,
  };

  const locale = currencyData.title === 'EUR' ? 'de-DE' : 'en-US';

  return parseFloat(value).toLocaleString(locale, options);
}

export function getRandomBetween(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

let faviconLinks;
export function changeFaviconAndTitle(faviconState, title) {
  if (!faviconLinks) {
    faviconLinks = document.head.querySelectorAll('link[rel$=icon]');
  }

  faviconLinks.forEach((link) => {
    link.href = link.href.replace(/([1-9])/, faviconState);
  });

  if (title) {
    document.title = title;
  }
}

export function getDateFromServer(date) {
  const dateInstance = new Date(date);

  return new Date(dateInstance.getTime() - dateInstance.getTimezoneOffset() * MS_PER_MINUTE);
}

export function getDiffStringFromDate(date) {
  const diff = Date.now() - new Date(date).getTime();

  const timeList = {
    day: Math.floor(diff / MS_PER_DAY),
    hour: Math.floor((diff % MS_PER_DAY) / MS_PER_HOUR),
    minute: Math.floor((diff % MS_PER_HOUR) / MS_PER_MINUTE),
  };

  let string = '';

  //  eslint-disable-next-line no-restricted-syntax
  for (const time in timeList) {
    if (timeList[time]) {
      string = `${timeList[time]} ${time > 1 ? time : `${time}s`}`;
      break;
    }
  }

  return {
    ...timeList,
    string,
  };
}

export function stringTruncate(str, maxLength = 100) {
  return str.length > maxLength
    ? `${str.slice(0, 6)}...${str.slice(-6)}`
    : str;
}

export function getRoundedDate(minutes, date) {
  const localDate = date ? new Date(date) : new Date();
  const ms = 1000 * 60 * minutes; // convert minutes to ms

  return new Date(Math.floor(localDate.getTime() / ms) * ms);
}

export function convertTextareaToHTML(content) {
  return content.replace(/\n+/g, '<br/>');
}

export async function addBuyLogs(error) {
  const { API_URI } = AppModel;

  await axios.post(`${API_URI}/log/add`, JSON.stringify(error?.response) + JSON.stringify(error?.message));
}

/**
 * Convert event time to timezone
 * @param {string} time - The time to convert
 * @param {string} timezone - The timezone to convert to
 * @returns {string} The converted time
 */
export function convertEventTimeToTimezone(time, timezone) {
  // Parse the time in UTC
  const utcTime = dayjs.utc(time);

  // If timezone is in offset format (e.g., "+01:00" or "-05:00")
  if (/^[+-]\d{2}:\d{2}$/.test(timezone)) {
    // Extract hours and minutes from timezone offset
    const [, sign, hours, minutes] = timezone.match(/^([+-])(\d{2}):(\d{2})$/);

    // Calculate total minutes for offset
    const totalMinutes = (parseInt(hours, 10) * 60 + parseInt(minutes, 10)) * (sign === '+' ? 1 : -1);

    // Add the offset to UTC time
    return utcTime.add(totalMinutes, 'minute').format('YYYY-MM-DD HH:mm:ss');
  }

  // For named timezones (if any), just return the UTC time
  return utcTime.format('YYYY-MM-DD HH:mm:ss');
}
